import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { FormEvent } from 'react';
import { Link } from 'react-router-dom';
import type { MarketingEvent } from '@bcc/shared';
import { AnimatePresence, motion } from 'framer-motion';

import { COMPANY_ADDRESS_SINGLE } from '../constants/companyContact';
import { PhoneField } from '../components/PhoneField';
import { apiUrl } from '../lib/apiUrl';
import { supabase } from '../lib/supabase';

const titlePink = 'text-[var(--header-shell-bg)]';

const SLIDESHOW_IMAGES = [
  'https://images.unsplash.com/photo-1475721027785-f74eccf877e2?auto=format&fit=crop&w=1200&q=82',
  'https://images.unsplash.com/photo-1505373877841-8d25f7d46678?auto=format&fit=crop&w=1200&q=82',
  'https://images.unsplash.com/photo-1515187029135-18ee286d815b?auto=format&fit=crop&w=1200&q=82',
  'https://images.unsplash.com/photo-1540039151733-d957a10d160e?auto=format&fit=crop&w=1200&q=82',
  'https://images.unsplash.com/photo-1560439514-4e9645039924?auto=format&fit=crop&w=1200&q=82',
  'https://images.unsplash.com/photo-1524178232363-1fb2b075b655?auto=format&fit=crop&w=1200&q=82',
  'https://images.unsplash.com/photo-1558002038-1055907df827?auto=format&fit=crop&w=1200&q=82',
  'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?auto=format&fit=crop&w=1200&q=82',
  'https://images.unsplash.com/photo-1504384308090-c894fdcc538d?auto=format&fit=crop&w=1200&q=82',
] as const;

type ScheduledEvent = {
  id: string;
  monthLabel: string;
  sortKey: string;
  dayDisplay: string;
  monthShort: string;
  title: string;
  description: string;
  venue: string;
  timeRange: string;
  image: string;
  isOnline: boolean;
  eventUrl: string | null;
  /** Supabase marketing_events.id — RSVP form when set */
  apiId?: string;
};

function mapApiToScheduled(rows: MarketingEvent[]): ScheduledEvent[] {
  return rows.map((e) => {
    const start = new Date(e.starts_at);
    const monthLong = new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric', timeZone: e.timezone }).format(start);
    const dayNum = start.getDate();
    const dayEnd = e.ends_at ? new Date(e.ends_at).getDate() : dayNum;
    const dayDisplay = dayEnd !== dayNum ? `${String(dayNum).padStart(2, '0')}–${String(dayEnd).padStart(2, '0')}` : String(dayNum).padStart(2, '0');
    const monthShort = new Intl.DateTimeFormat('en-US', { month: 'short', timeZone: e.timezone }).format(start);
    const img = e.image_path
      ? supabase.storage.from('event-assets').getPublicUrl(e.image_path).data.publicUrl
      : SLIDESHOW_IMAGES[0];
    return {
      id: e.id,
      apiId: e.id,
      monthLabel: monthLong,
      sortKey: e.starts_at.slice(0, 10),
      dayDisplay,
      monthShort,
      title: e.title,
      description: e.description || '',
      venue: e.venue,
      timeRange: e.time_label || '',
      image: img,
      isOnline: Boolean(e.is_online),
      eventUrl: e.event_url,
    };
  });
}

function EventRsvpForm({ eventId }: { eventId: string }) {
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [idleSuccess, setIdleSuccess] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setFormError(null);
    try {
      const res = await fetch(apiUrl(`/api/public/events/${eventId}/rsvp`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ full_name: name.trim(), email: email.trim(), phone: phone.trim() || undefined }),
      });
      if (!res.ok) throw new Error('RSVP failed');
      const data = (await res.json()) as { already?: boolean };
      const msg = data.already ? 'You are already registered — confirmation resent to email.' : 'RSVP received — check your email.';
      setName('');
      setEmail('');
      setPhone('');
      setIdleSuccess(msg);
      setShowForm(false);
    } catch {
      setFormError('Could not submit. Try again later.');
    }
  }

  if (!showForm) {
    return (
      <div className="mt-4 space-y-2">
        {idleSuccess ? (
          <p className="text-xs font-medium text-emerald-200/95" role="status">
            {idleSuccess}
          </p>
        ) : null}
        <button
          type="button"
          onClick={() => {
            setIdleSuccess(null);
            setFormError(null);
            setShowForm(true);
          }}
          className={`w-full rounded-lg border border-white/20 bg-white/[0.06] px-4 py-2.5 text-center font-display text-[0.62rem] font-bold uppercase tracking-[0.14em] ${titlePink} shadow-sm ring-1 ring-white/10 transition hover:bg-white/10 sm:w-auto`}
        >
          Click to RSVP
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={(evt) => void submit(evt)} className="mt-4 space-y-2">
      <p className="font-display text-[0.58rem] font-semibold uppercase tracking-[0.12em] text-[var(--home-muted-on-dark)]">
        RSVP
      </p>
      <input
        required
        placeholder="Full name"
        value={name}
        onChange={(ev) => setName(ev.target.value)}
        className="w-full rounded-lg border border-white/15 bg-white/[0.92] px-3 py-2 text-sm text-neutral-900"
      />
      <input
        required
        type="email"
        placeholder="Email"
        value={email}
        onChange={(ev) => setEmail(ev.target.value)}
        className="w-full rounded-lg border border-white/15 bg-white/[0.92] px-3 py-2 text-sm text-neutral-900"
      />
      <PhoneField
        placeholder="Phone (optional)"
        value={phone}
        onChange={setPhone}
      />
      <div className="flex flex-wrap gap-2 pt-1">
        <button
          type="submit"
          className={`font-display text-[0.6rem] font-semibold uppercase tracking-[0.14em] ${titlePink} rounded-md bg-white/10 px-4 py-2 hover:bg-white/15`}
        >
          Submit RSVP
        </button>
        <button
          type="button"
          onClick={() => {
            setShowForm(false);
            setFormError(null);
          }}
          className="rounded-md border border-white/15 px-4 py-2 font-body text-xs text-[var(--home-muted-on-dark)] hover:bg-white/5"
        >
          Cancel
        </button>
      </div>
      {formError ? <p className="text-xs text-rose-200/90">{formError}</p> : null}
    </form>
  );
}

const SCHEDULED_EVENTS: ScheduledEvent[] = [
  {
    id: 'ev-1',
    monthLabel: 'May 2026',
    sortKey: '2026-05-04',
    dayDisplay: '04',
    monthShort: 'May',
    title: 'Accredited investor briefing — multifamily & private placements',
    description:
      'Regulatory-friendly overview of 506(c) offerings, sponsor diligence, and how we work with accredited investors — capped attendance.',
    venue: `Breckenridge Capital · ${COMPANY_ADDRESS_SINGLE}`,
    timeRange: '9:30 am – 11:30 am',
    image: SLIDESHOW_IMAGES[0],
    isOnline: false,
    eventUrl: null,
  },
  {
    id: 'ev-2',
    monthLabel: 'May 2026',
    sortKey: '2026-05-14',
    dayDisplay: '14',
    monthShort: 'May',
    title: 'Self-directed IRA / 401(k) workshop with investor relations',
    description: 'How self-directed retirement accounts partner with private placements, paperwork timing, and what to expect at subscription.',
    venue: 'Virtual · RSVP for link',
    timeRange: '1:00 pm – 2:30 pm',
    image: SLIDESHOW_IMAGES[4],
    isOnline: true,
    eventUrl: null,
  },
  {
    id: 'ev-3',
    monthLabel: 'May 2026',
    sortKey: '2026-05-22',
    dayDisplay: '22',
    monthShort: 'May',
    title: 'Live Q&A — underwriting, 506(c), and allocation process',
    description: 'Open forum on our underwriting standards, allocation mechanics, and communications during a live raise.',
    venue: 'Forum suite · Main office · Tampa MSA',
    timeRange: '4:00 pm – 6:00 pm',
    image: SLIDESHOW_IMAGES[2],
    isOnline: false,
    eventUrl: null,
  },
  {
    id: 'ev-4',
    monthLabel: 'June 2026',
    sortKey: '2026-06-07',
    dayDisplay: '07–08',
    monthShort: 'Jun',
    title: 'Two-day deep dive — sponsor diligence & portfolio fit',
    description: 'Institutional-style diligence agenda: financial, physical, and legal workstreams with workbook materials.',
    venue: 'Tampa conference block · details with RSVP',
    timeRange: 'Day 1: 9:00 am – 4:00 pm · Day 2: 9:00 am – 12:00 pm',
    image: SLIDESHOW_IMAGES[6],
    isOnline: false,
    eventUrl: null,
  },
];

function IconPin({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" width={15} height={15} fill="none" stroke="currentColor" strokeWidth={2} className={className} aria-hidden>
      <path d="M12 21s7-4.35 7-10a7 7 0 10-14 0c0 5.65 7 10 7 10z" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="12" cy="11" r="2" />
    </svg>
  );
}

function IconClock({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" width={15} height={15} fill="none" stroke="currentColor" strokeWidth={2} className={className} aria-hidden>
      <circle cx="12" cy="12" r="8" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M12 8v4l3 3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function groupByMonthLabel(events: ScheduledEvent[]): { monthLabel: string; items: ScheduledEvent[] }[] {
  const map = new Map<string, ScheduledEvent[]>();
  for (const e of events) {
    const list = map.get(e.monthLabel);
    if (list) list.push(e);
    else map.set(e.monthLabel, [e]);
  }
  return Array.from(map.entries()).map(([monthLabel, items]) => ({
    monthLabel,
    items: [...items].sort((a, b) => a.sortKey.localeCompare(b.sortKey)),
  }));
}

const CAROUSEL_AUTOPLAY_MS = 6000;

function EventGalleryCarousel({ slides, children }: { slides: readonly string[]; children: ReactNode }) {
  const total = slides.length;
  const [index, setIndex] = useState(0);

  const goPrev = () => setIndex((i) => (i - 1 + total) % total);
  const goNext = useCallback(() => setIndex((i) => (i + 1) % total), [total]);

  useEffect(() => {
    const id = window.setInterval(() => {
      setIndex((i) => (i + 1) % total);
    }, CAROUSEL_AUTOPLAY_MS);
    return () => window.clearInterval(id);
  }, [total]);

  return (
    <div
      className="relative w-full overflow-hidden rounded-2xl border border-white/[0.08] bg-neutral-950 ring-1 ring-white/15 shadow-[0_24px_60px_-40px_rgba(0,0,0,0.55)]"
      role="region"
      aria-roledescription="carousel"
      aria-label="Event gallery slideshow"
    >
      <div className="absolute inset-0 min-h-[22rem] sm:min-h-[26rem] md:min-h-[28rem] lg:min-h-[31rem]">
        <AnimatePresence initial={false} mode="wait">
          <motion.img
            key={index}
            src={slides[index]}
            alt=""
            className="absolute inset-0 h-full w-full object-cover"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
          />
        </AnimatePresence>
      </div>

      {/* Readability scrim — stronger on the left where copy sits */}
      <div
        className="pointer-events-none absolute inset-0 bg-gradient-to-b from-black/55 via-black/35 to-black/70 sm:bg-gradient-to-r sm:from-black/82 sm:via-black/45 sm:to-black/20"
        aria-hidden
      />

      <div className="relative z-10 flex min-h-[22rem] flex-col justify-end sm:min-h-[26rem] md:min-h-[28rem] md:justify-center lg:min-h-[31rem]">
        <div className="w-full px-5 pb-16 pt-10 sm:px-8 sm:pb-14 md:max-w-[min(36rem,92%)] md:px-10 md:py-14 lg:px-12">{children}</div>
      </div>

      <button
        type="button"
        onClick={goPrev}
        aria-label="Previous slide"
        className="absolute left-2 top-1/2 z-20 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-black/50 text-white shadow-lg ring-1 ring-white/20 backdrop-blur-sm transition hover:bg-black/65 md:left-4"
      >
        <svg viewBox="0 0 24 24" width={20} height={20} fill="none" stroke="currentColor" strokeWidth={2} aria-hidden>
          <path d="M15 6l-6 6 6 6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      <button
        type="button"
        onClick={goNext}
        aria-label="Next slide"
        className="absolute right-2 top-1/2 z-20 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-black/50 text-white shadow-lg ring-1 ring-white/20 backdrop-blur-sm transition hover:bg-black/65 md:right-4"
      >
        <svg viewBox="0 0 24 24" width={20} height={20} fill="none" stroke="currentColor" strokeWidth={2} aria-hidden>
          <path d="M9 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      <div className="absolute bottom-0 left-0 right-0 z-20 flex items-center justify-center gap-2 bg-gradient-to-t from-black/55 to-transparent px-3 pb-4 pt-10">
        {slides.map((_, j) => (
          <button
            key={j}
            type="button"
            aria-label={`Go to slide ${j + 1}`}
            aria-current={j === index ? 'true' : undefined}
            onClick={() => setIndex(j)}
            className={`h-2 rounded-full transition-all focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--header-shell-bg)] ${
              j === index ? 'w-7 bg-[var(--header-shell-bg)]' : 'w-2 bg-white/35 hover:bg-white/55'
            }`}
          />
        ))}
      </div>
    </div>
  );
}

/** Public /events — marketing layout only; CRM, attendance, and investor tickets live in admin / dashboard. */
export function Events() {
  const [apiEvents, setApiEvents] = useState<MarketingEvent[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch(apiUrl('/api/public/events'));
        if (!res.ok) return;
        const data = (await res.json()) as { upcoming?: MarketingEvent[]; past?: MarketingEvent[] };
        if (!cancelled) setApiEvents(data.upcoming ?? []);
      } catch {
        if (!cancelled) setApiEvents([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const schedule = useMemo(() => {
    if (apiEvents && apiEvents.length > 0) return mapApiToScheduled(apiEvents);
    return SCHEDULED_EVENTS;
  }, [apiEvents]);

  const groups = useMemo(() => groupByMonthLabel(schedule), [schedule]);

  return (
    <div className="pb-24">
      {/* Hero — single card: slideshow + overlaid copy */}
      <section className="relative overflow-hidden border-b border-white/[0.08]">
        <div className="relative mx-auto max-w-[90rem] px-4 py-12 md:px-8 md:py-16 lg:px-10 lg:py-20">
          <EventGalleryCarousel slides={SLIDESHOW_IMAGES}>
            <div className="space-y-5 text-pretty drop-shadow-[0_2px_24px_rgba(0,0,0,0.65)]">
              <div className="h-1 w-12 rounded-full bg-[var(--header-shell-bg)] shadow-[0_0_16px_rgba(244_114_182/0.5)]" aria-hidden />
              <h1 className="font-display text-[clamp(1.75rem,4.2vw,2.75rem)] font-bold uppercase leading-[1.05] tracking-[0.05em]">
                <span className={titlePink}>Upcoming </span>
                <span className="text-white">events</span>
              </h1>
              <p className="max-w-xl font-body text-[0.95rem] leading-relaxed text-white/80 md:text-base">
                Investor education sessions, regulatory-friendly briefings, and office-hosted Q&amp;As. RSVP required — capacity
                limited for accredited audiences.
              </p>
            </div>
            <div className="mt-6 space-y-3">
              <Link
                to="/contact"
                id="tickets"
                className="inline-flex items-center justify-center rounded-full bg-[var(--header-shell-bg)] px-8 py-3.5 font-display text-[0.65rem] font-bold uppercase tracking-[0.14em] text-white shadow-[0_10px_36px_-8px_rgb(244_114_182/0.55)] ring-1 ring-white/25 transition hover:brightness-110"
              >
                View your tickets
              </Link>
              <p className="max-w-md font-body text-xs leading-relaxed text-white/65">
                Ticket / RSVP requests route through investor relations — not a public box office.
              </p>
            </div>
          </EventGalleryCarousel>
        </div>
      </section>

      {/* CTA strip — grey glass (matches deals / IRA panels) */}
      <section className="border-b border-white/[0.08] bg-[var(--home-zone-dark)]/50 backdrop-blur-md ring-1 ring-white/[0.04] ring-inset">
        <div className="mx-auto flex max-w-[90rem] flex-col gap-5 px-4 py-6 md:flex-row md:items-center md:justify-between md:gap-10 md:px-8 lg:px-10">
          <p className="font-display text-[clamp(0.9rem,1.8vw,1.05rem)] font-bold uppercase leading-snug tracking-[0.06em] text-[var(--home-text-on-dark)]">
            Book Breckenridge Capital for your investor event or luncheon
          </p>
          <Link
            to="/contact"
            className="inline-flex shrink-0 items-center justify-center rounded-xl bg-[var(--header-shell-bg)] px-8 py-3.5 font-display text-[0.65rem] font-bold uppercase tracking-[0.12em] text-white shadow-[0_10px_30px_-8px_rgb(244_114_182/0.5)] ring-1 ring-white/25 transition hover:brightness-110"
          >
            Book briefing
          </Link>
        </div>
      </section>

      {/* Schedule — anchor for deep links (e.g. corner “RSVP / reminders”) */}
      <section
        id="events-schedule"
        className="mx-auto max-w-[90rem] scroll-mt-28 px-4 pt-14 md:px-8 lg:px-10"
        aria-labelledby="schedule-heading"
      >
        <h2 id="schedule-heading" className="sr-only">
          Event schedule
        </h2>

        {groups.map(({ monthLabel, items }) => (
          <div key={monthLabel} className="mb-14 last:mb-4">
            <h3 className="font-display text-lg font-bold uppercase tracking-[0.14em] text-[var(--home-text-on-dark)] md:text-xl">
              {monthLabel}
            </h3>
            <ul className="mt-6 space-y-4">
              {items.map((ev) => (
                <li key={ev.id}>
                  <article className="overflow-hidden rounded-2xl border border-white/[0.08] bg-[var(--home-zone-dark)]/50 shadow-[0_24px_60px_-40px_rgba(0,0,0,0.55)] ring-1 ring-white/[0.04] backdrop-blur-sm transition hover:border-white/[0.12]">
                    <div className="flex flex-col sm:flex-row">
                      <div className="relative aspect-[16/10] w-full shrink-0 overflow-hidden sm:aspect-auto sm:h-auto sm:w-64 sm:min-h-[15rem] md:w-80 md:min-h-[17rem] lg:w-96 lg:min-h-[18rem]">
                        <img
                          src={ev.image}
                          alt=""
                          className="h-full w-full object-cover"
                          loading="lazy"
                        />
                      </div>
                      <div className="flex min-w-0 flex-1 flex-col gap-4 p-4 sm:flex-row sm:items-stretch sm:gap-0 sm:p-0 sm:pl-1">
                        <div className="flex shrink-0 flex-row items-center gap-4 border-b border-white/10 pb-4 sm:w-[5.5rem] sm:flex-col sm:justify-center sm:border-b-0 sm:border-r sm:border-white/10 sm:px-3 sm:py-4 sm:pb-0 md:w-28">
                          <span className="font-display text-[clamp(1.75rem,3vw,2.25rem)] font-bold leading-none tabular-nums text-[var(--header-shell-bg)]">
                            {ev.dayDisplay}
                          </span>
                          <span className="font-display text-[0.7rem] font-semibold uppercase tracking-[0.12em] text-[var(--home-miami-blue)]">
                            {ev.monthShort}
                          </span>
                        </div>
                        <div className="min-w-0 flex-1 border-t border-white/10 px-1 py-4 sm:border-t-0 sm:px-6 sm:py-5">
                          <div className="flex flex-wrap items-center gap-2">
                            <span
                              className={`rounded-md px-2 py-0.5 font-display text-[0.58rem] font-bold uppercase tracking-[0.12em] ring-1 ${
                                ev.isOnline
                                  ? 'bg-sky-500/15 text-sky-100 ring-sky-400/30'
                                  : 'bg-white/10 text-white/95 ring-white/15'
                              }`}
                            >
                              {ev.isOnline ? 'Online' : 'In person'}
                            </span>
                          </div>
                          <h4 className="mt-2 font-display text-[0.95rem] font-bold uppercase leading-snug tracking-[0.05em] text-[var(--home-text-on-dark)] md:text-base">
                            {ev.title}
                          </h4>
                          {ev.description ? (
                            <p className="mt-3 font-body text-sm leading-relaxed text-[var(--home-muted-on-dark)]">{ev.description}</p>
                          ) : null}
                          <p className="mt-3 flex items-start gap-2 font-body text-sm leading-relaxed text-[var(--home-muted-on-dark)]">
                            <IconPin className="mt-0.5 shrink-0 text-[var(--home-miami-blue)] opacity-90" />
                            <span>{ev.venue}</span>
                          </p>
                          <p className="mt-2 flex items-start gap-2 font-body text-sm text-[var(--home-muted-on-dark)]">
                            <IconClock className="mt-0.5 shrink-0 text-[var(--home-miami-blue)] opacity-90" />
                            <span>{ev.timeRange}</span>
                          </p>
                          {ev.isOnline && ev.eventUrl ? (
                            <a
                              href={ev.eventUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="mt-3 inline-flex font-body text-sm font-medium text-[var(--home-miami-blue)] underline underline-offset-2 hover:brightness-110"
                            >
                              Online join link
                            </a>
                          ) : null}
                          {ev.apiId ? (
                            <EventRsvpForm eventId={ev.apiId} />
                          ) : (
                            <div className="mt-4">
                              <Link
                                to="/contact"
                                className={`font-display text-[0.6rem] font-semibold uppercase tracking-[0.14em] ${titlePink} underline-offset-4 hover:underline`}
                              >
                                RSVP / details
                              </Link>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </article>
                </li>
              ))}
            </ul>
          </div>
        ))}

        <p className="pb-8 font-body text-sm leading-relaxed text-[var(--home-muted-on-dark)]">
          Times shown in Eastern. Events subject to change; formal invites supersede this page. Accredited investors only where
          offerings require it.
        </p>
      </section>
    </div>
  );
}
